import { useMsal } from '@azure/msal-react';
import copy from 'copy-to-clipboard';
import Markdown from 'markdown-to-jsx';
import { useEffect, useRef, useState } from 'react';
import CopySVG from 'src/assets/images/icons/CopySVG';
import SendSVG from 'src/assets/images/icons/SendSVG';
import { RagBot, UserInfo, checkRagaasBot, checkRagaasUser, queryRagaas } from 'src/pages/user-files/service';
import idleImage from '../../../assets/images/image.png';
import speakingVideo from '../../../assets/images/video.mp4';
import './avatar.css';


interface CommonTabContentProps {
  defaultPrompt?: string;
}

const RagaasTabContent: React.FC<CommonTabContentProps> = ({ defaultPrompt = '' }) => {

  const [promptValue, setPromptValue] = useState(defaultPrompt);
  const [copyButtonText, setCopyButtonText] = useState('Copy');
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState(' ');
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const { instance } = useMsal();
  const accounts = instance.getAllAccounts();

  const [user, setUser] = useState<UserInfo>({
    first_name: accounts[0].name ? accounts[0].name.split(", ")[1] : "",
    last_name: accounts[0].name ? accounts[0].name.split(", ")[0] : "",
    user_email: accounts[0].username,
    user_id: accounts[0].homeAccountId
  });
  const [bots, setBots] = useState<RagBot[]>([{
    bot_id: "",
    bot_name: "",
    bot_description: ""
  }]);
  const [botNames, setBotNames] = useState<String[]>([]);
  const [selectedBot, setSelectedBot] = useState<RagBot>({
    bot_id: "",
    bot_name: "",
    bot_description: ""
  });

  const syncBots = async () => {
    await checkRagaasUser(user);
    const botList = await checkRagaasBot(user);
    const nameList: String[] = botList.map((entry: RagBot) => entry.bot_name);
    const filteredList = nameList.filter((entry, index) => nameList.indexOf(entry) === index);
    setBots(botList);
    setSelectedBot(botList[0]);
    setBotNames(filteredList);
  }

  useEffect(() => {
    setPromptValue(defaultPrompt);
    syncBots();
  }, [defaultPrompt]);

  const query = async (prompt: string) => {
    setLoading(true);
    let history = responseData;
    history = history + "<p><b>" + user.first_name + ":</b><br>" + prompt + "<br><br></p>";
    console.log(history);
    setResponseData(history);
    const queryResponse = await queryRagaas(user, prompt, selectedBot);
    history = history + "<p><b>Ray:</b><br>" + queryResponse.response + "<br><br></p>";
    const source = queryResponse.source
    const linkPattern = /KB\d{7}/
    if (linkPattern.test(source)) {
      const link = "https://brightspeed.service-now.com/esc?id=kb_article&sysparm_article=" + linkPattern.exec(source)
      history = history + "<p><b>Article Link:</b><br><a href='" + link + "'>" + link + "</a><br><br></p>"
    }
    console.log(history);
    setResponseData(history);
    setLoading(false);




    try {
      if (isPaused) {
        setIsPaused(false);
        window.speechSynthesis.resume();
        if (videoRef.current && videoRef.current.paused) {
          videoRef.current.play();
        }
        return;
      }

      setIsPlaying(true);
      setIsPaused(false);

      const video = videoRef.current;

      if (video) {
        video.currentTime = 0;
        video.play().catch((err) => console.error('Error playing video:', err));
      }

      const utterance = new SpeechSynthesisUtterance(queryResponse.response);
      utterance.lang = 'en-US';
      utterance.rate = 1;
      utterance.pitch = 1;

      utterance.onstart = () => {
        if (video && video.paused) {
          video.play();
        }
      };

      utterance.onend = () => {
        setIsPlaying(false);
        if (video) {
          video.pause();
        }
      };

      window.speechSynthesis.speak(utterance);
    } catch (error) {
      console.error('Error fetching data from API:', error);
      setIsPlaying(false);
    }

  }

  // copy response on click
  const copyToClipboard = () => {
    if (responseData) {
      const responseDataString = responseData as string;
      const plainText = responseDataString.replace(/(?:__|[*#])|\[(.*?)\]\(.*?\)/g, '$1');
      copy(plainText);
      setCopyButtonText('Copied');
      setTimeout(() => {
        setCopyButtonText('Copy');
      }, 1000);
    }
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOption = e.target.value;
    setSelectedBot(bots.filter((entry: RagBot) => entry.bot_name == selectedOption)[0]);
  };

  return (
    <div className="max-w-2xl">
      <div className="mb-4 border rounded-md border-gray-300 bg-white">
        <div className="px-3 py-2 sm:px-3">
          <h4 className="flex items-center gap-2 text-sm font-normal leading-6 text-gray-900">
            Select RAG Bot
          </h4>
        </div>
        <div className="p-4 border-t">
          <select className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-1 focus:ring-gray-600 sm:text-sm sm:leading-6" onChange={handleSelectChange}>
            {botNames.map((entry) => <option>{entry}</option>)}
          </select>
        </div>
      </div>

      {responseData && (
        <div className="mt-4">
          <div className="flex justify-between mb-4 items-center">
            <h3 className="font-bold">Ask Ray:</h3>
            <button
              onClick={copyToClipboard}
              type="button"
              className="border px-2 py-1 text-sm font-semibold rounded flex gap-1 items-center"
            >
              <CopySVG />
              {copyButtonText}
            </button>
          </div>
          <div>
            <div className="responseWrapper p-4 border rounded-md border-gray-300 overflow-auto py-100 px-200">
              <Markdown>{responseData}</Markdown>
            </div>
            <div className="rag-avatar">
              {isPlaying ? (
                <video className="avatar-video" ref={videoRef} src={speakingVideo} loop={false} muted autoPlay><source src={speakingVideo} type="video/mp4" /> </video>
              ) : (
                <img src={idleImage} alt="Avatar" className="avatar-image" />
              )}
            </div>
          </div>
        </div>
      )}

      <div>
        {/* <div className="mb-4 border rounded-md border-gray-300 bg-white"> */}
        <div className="p-4 border-t">
          <textarea
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
            value={promptValue}
            onChange={(e) => setPromptValue(e.target.value)}
            placeholder="Type Message"
          />
          <button
            onClick={() => query(promptValue)}
            type="button"
            className="inline-flex justify-center rounded-md bg-[#f8c246] px-4 py-2.5 text-sm font-semibold text-black shadow-sm hover:bg-[#f8c246] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            {loading ? (
              <span role="status">Loading...</span>
            ) : (
              <span className="flex gap-2 items-center">
                Send
                <SendSVG />
              </span>
            )}
          </button>
          {/* </div> */}
        </div>




      </div>
    </div>
  );
};

export default RagaasTabContent;